/**
 * @module components/Settings/PreferencesTab
 * @description Pestaña de preferencias de UI del usuario.
 *
 * Permite configurar: tema claro/oscuro (persiste en localStorage via ThemeContext)
 * y formato de fecha preferido (DD/MM/YYYY, MM/DD/YYYY, YYYY-MM-DD).
 * Las preferencias de formato se guardan en `users.metadata.preferences`.
 *
 * Usa `forwardRef` + `useImperativeHandle` para exponer `saveChanges()` al `SettingsModal`.
 *
 * @see ThemeContext para el toggle de tema oscuro/claro
 * @see SettingsModal que centraliza el guardado
 */
import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { Moon, Sun, Globe, Calendar } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';

export const PreferencesTab = forwardRef<{ saveChanges: () => Promise<void> }>((props, ref) => {
  const { isDark, toggleTheme } = useTheme();
  const { user, updateMetadata } = useAuth();
  const [dateFormat, setDateFormat] = useState<string>(
    user?.metadata?.preferences?.dateFormat || 'DD/MM/YYYY'
  );
  const [saveSuccess, setSaveSuccess] = useState(false);

  const handleSavePreferences = async () => {
    try {
      await updateMetadata({
        preferences: {
          ...user?.metadata?.preferences,
          dateFormat,
          theme: isDark ? 'dark' : 'light'
        }
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error guardando preferencias:', err);
      throw err;
    }
  };

  // Expose saveChanges method to parent via ref
  useImperativeHandle(ref, () => ({
    saveChanges: handleSavePreferences
  }));

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Preferencias de la Aplicación
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Personaliza la apariencia y comportamiento del sistema
        </p>
      </div>

      {/* Theme */}
      <div className="pb-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              {isDark ? (
                <Moon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              ) : (
                <Sun className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              )}
              <h4 className="font-medium text-gray-900 dark:text-white">Tema de la interfaz</h4>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Elige entre tema claro u oscuro
            </p>
          </div>
          <button
            onClick={toggleTheme}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              isDark ? 'bg-blue-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                isDark ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <button
            onClick={() => isDark && toggleTheme()}
            className={`p-3 rounded-lg border-2 transition-colors ${
              !isDark
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <Sun className="w-5 h-5 mb-2 text-gray-700 dark:text-gray-300" />
            <p className="text-sm font-medium text-gray-900 dark:text-white">Claro</p>
          </button>
          <button
            onClick={() => !isDark && toggleTheme()}
            className={`p-3 rounded-lg border-2 transition-colors ${
              isDark
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <Moon className="w-5 h-5 mb-2 text-gray-700 dark:text-gray-300" />
            <p className="text-sm font-medium text-gray-900 dark:text-white">Oscuro</p>
          </button>
        </div>
      </div>

      {/* Language (coming soon) */}
      <div className="pb-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Globe className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h4 className="font-medium text-gray-900 dark:text-white">Idioma</h4>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Selecciona el idioma de la interfaz
            </p>
          </div>
        </div>
        <div className="mt-4">
          <select
            disabled
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 cursor-not-allowed"
          >
            <option>Español (ES)</option>
          </select>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            Próximamente: Inglés, Portugués
          </p>
        </div>
      </div>

      {/* Date format */}
      <div>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h4 className="font-medium text-gray-900 dark:text-white">Formato de fecha</h4>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Formato en que se muestran las fechas
            </p>
          </div>
        </div>
        <div className="mt-4">
          <select
            value={dateFormat}
            onChange={(e) => setDateFormat(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="DD/MM/YYYY">DD/MM/YYYY (31/12/2024)</option>
            <option value="MM/DD/YYYY">MM/DD/YYYY (12/31/2024)</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD (2024-12-31)</option>
          </select>
        </div>
      </div>

      {saveSuccess && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <p className="text-sm font-medium text-green-900 dark:text-green-100">
            ✓ Preferencias guardadas exitosamente
          </p>
        </div>
      )}
    </div>
  );
});
